import React, { useState, useEffect } from "react";
import { Card, Row, Col, Button, Table } from "react-bootstrap";
import { BsChevronLeft, BsChevronRight } from "react-icons/bs";
import { useSelector } from "react-redux";
import axios from "axios";
import { useGetLeaveRequestsByCollaboratorQuery } from "store/conge/congeApi";
import { useTranslation } from "react-i18next";
import "./AbsencesSection.scss";

const AbsencesSection = () => {
  const { t, i18n } = useTranslation();
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth());
  const [year, setYear] = useState(today.getFullYear());
  const [holidays, setHolidays] = useState([]);
  const [absences, setAbsences] = useState([]);
  const currentUser = useSelector((state) => state.auth.user);

  const weekDays = [
    "L",
    "M",
    "M",
    "J",
    "V",
    "S",
    "D",
    "L",
    "M",
    "M",
    "J",
    "V",
    "S",
    "D",
  ];

  // Récupération des jours fériés
  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        const response = await axios.get(
          `https://date.nager.at/api/v3/PublicHolidays/${year}/FR`
        );
        setHolidays(response.data.map((holiday) => holiday.date));
      } catch (error) {
        console.error(t("absences.error_fetching"), error);
      }
    };
    fetchHolidays();
  }, [year, t]);

  // RTK Query
  const { data, error, isLoading } = useGetLeaveRequestsByCollaboratorQuery(
    currentUser._id
  );

  useEffect(() => {
    if (data?.data) {
      const formattedAbsences = [];
      data.data.forEach((leaveRequest) => {
        leaveRequest.leaves.forEach((leave) => {
          const notes = leave.calendarNotes;
          Object.keys(notes).forEach((date) => {
            formattedAbsences.push({
              date,
              status: leaveRequest.isValide ? "validated" : "not-validated",
              type: leave.type,
              value: notes[date],
            });
          });
        });
      });
      formattedAbsences.sort((a, b) => new Date(a.date) - new Date(b.date));
      setAbsences(formattedAbsences);
    }
  }, [data]);

  const changeMonth = (direction) => {
    let newMonth = month + direction;
    let newYear = year;
    if (newMonth < 0) {
      newMonth = 11;
      newYear--;
    } else if (newMonth > 11) {
      newMonth = 0;
      newYear++;
    }
    setMonth(newMonth);
    setYear(newYear);
  };

  const renderCalendar = () => {
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    let firstDay = new Date(year, month, 1).getDay();
    if (firstDay === 0) firstDay = 7;

    const days = [];
    for (let i = 1; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="day empty"></div>);
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const currentDate = `${year}-${String(month + 1).padStart(
        2,
        "0"
      )}-${String(d).padStart(2, "0")}`;
      const dayOfWeek = new Date(year, month, d).getDay();
      const absence = absences.find((a) => a.date === currentDate);
      const isHoliday = holidays.includes(currentDate);

      let extraClass = "";
      if (dayOfWeek === 0 || dayOfWeek === 6) extraClass = "weekend";
      if (isHoliday) extraClass = "holiday";

      let status = "";
      let displayValue = d;
      if (absence) {
        status = absence.status;
        displayValue = absence.value === "0.5" ? `${d}½` : d;
      }

      days.push(
        <div key={d} className={`day ${status} ${extraClass}`}>
          {displayValue}
        </div>
      );
    }

    return days;
  };

  if (isLoading) return <p>{t("absences.loading")}</p>;
  if (error) return <p>{t("absences.error_fetching")}</p>;

  return (
    <div className="absences-container">
      <Card className="calendar-card m-4">
        <Row className="align-items-center justify-content-between mb-3">
          <Col xs="auto">
            <Button variant="light" onClick={() => changeMonth(-1)}>
              <BsChevronLeft />
            </Button>
          </Col>
          <Col xs="auto">
            <h6>
              {new Date(year, month).toLocaleString("fr-FR", {
                month: "long",
                year: "numeric",
              })}
            </h6>
          </Col>
          <Col xs="auto">
            <Button variant="light" onClick={() => changeMonth(1)}>
              <BsChevronRight />
            </Button>
          </Col>
        </Row>

        <div className="calendar-grid week-header">
          {weekDays.map((day, i) => (
            <div key={i} className="day header">
              {day}
            </div>
          ))}
        </div>
        <div className="calendar-grid">{renderCalendar()}</div>

        <div className="legend mt-2">
          <span className="box validated"></span>{" "}
          {t("absences.legend.validated")}
          <span className="box not-validated ms-3"></span>{" "}
          {t("absences.legend.not_validated")}
          <span className="box weekend ms-3"></span>{" "}
          {t("absences.legend.weekend")}
          <span className="box holiday ms-3"></span>{" "}
          {t("absences.legend.holiday")}
        </div>
      </Card>

      <Card className="mt-4 m-4">
        <Card.Body>
          <h6>
            {t("absences.table.title")} ({absences.length})
          </h6>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>{t("absences.table.period")}</th>
                <th>{t("absences.table.status")}</th>
              </tr>
            </thead>
            <tbody>
              {absences.map((a, index) => (
                <tr key={index}>
                  <td>{a.date}</td>
                  <td
                    className={
                      a.status === "validated" ? "text-success" : "text-warning"
                    }>
                    {a.status === "validated"
                      ? t("absences.table.validated")
                      : t("absences.table.pending")}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </div>
  );
};

export default AbsencesSection;
